import { RequestPropertyIds } from '../data/BackendEntityConstants';
import { DCSP, DCSP_LLE, IPS, PAC, RFXTypes } from '../data/BusinessConstants';
import { WORKFLOW_TYPES } from '../data/RFXCommonStepsData';
import {
    NullableString,
    NullableStringArray
} from '../interfaces';
import { RFXRequest } from '../interfaces/RFXBackendModel';
import {
    RFXRequestGeneralInformation,
} from '../interfaces/RFXRequestForm';
import { getBusinessId, getBusinessIdFromBackendModel } from './BusinessUtils';
import { extractPropertyOrElseDefault } from './RFXUtils';

/**
 * Retrieves the first workflow type for the given array of business IDs.
 *
 * @param businessIds - An array of nullable string values representing business IDs.
 * @returns The first workflow type associated with the first non-null business ID in the array, or null if no workflow types are found.
 */
export const getWorkflowForBusinessIds = (businessIds: NullableStringArray): NullableString => {
    if (!businessIds || businessIds.length === 0) {
        return null;
    }
    const defaultBusinessId = businessIds[0];

    if (!WORKFLOW_TYPES[defaultBusinessId]) {
        return null;
    }
    const workflows: string[] = WORKFLOW_TYPES[defaultBusinessId];
    if (workflows.length === 0) {
        return null;
    }

    return workflows[0];
}

/**
 * Retrieves the first workflow type for the given business ID.
 *
 * @param businessId - A string representing a business ID.
 * @returns The first workflow type associated with the business ID, or null if no workflow types are found.
 */
export const getWorkflowForBusinessId = (businessId: string|undefined): NullableString => {
    if (!businessId || !WORKFLOW_TYPES[businessId]) {
        return null;
    }
    const workflows: string[] = WORKFLOW_TYPES[businessId];
    if (workflows.length === 0) {
        return null;
    }

    return workflows[0];
}

/**
 * Determines if multiple regions should be supported for a given workflow.
 * @param workflow - The workflow to check.
 * @returns True if the workflow supports multiple regions, false otherwise.
 */
export const shouldSupportMultipleRegionsForWorkflow = (workflow: NullableString | undefined): boolean => {
    switch (workflow) {
        case IPS:
        case DCSP: return true;
        default: break;
    }
    return false;
}

/**
 * Determines if Incoterm should be supported for a given workflow.
 * @param workflow - The workflow to check.
 * @returns True if the workflow supports Incoterm, false otherwise.
 */
export const shouldSupportIncotermForWorkflow = (workflow: NullableString | undefined, rfxType: string): boolean => {
    if (rfxType !== RFXTypes.RFQ) {
        return false;
    }
    switch (workflow) {
        case PAC: return true;
        case DCSP_LLE: return true;
        default: break;
    }
    return false;
}

/**
 * Determines if currency check should be supported for a given workflow.
 * @param workflow - The workflow to check.
 * @returns True if the workflow supports currency check, false otherwise.
 */
export const shouldSupportCurrencyCheckForWorkflow = (workflow: NullableString | undefined, rfxType: string): boolean => {
    if (rfxType !== RFXTypes.RFQ) {
        return false;
    }
    switch (workflow) {
        case PAC: return true;
        case DCSP_LLE: return true;
        default: break;
    }
    return false;
}

/**
 * Gets the workflow from general information, falling back to businessId if workflow is not set.
 * @param generalInformation - The general information object.
 * @returns The workflow or businessId.
 */
export const getWorkflowFromGeneraInfo = (generalInformation: RFXRequestGeneralInformation): string => {
    const workflow: NullableString|undefined = generalInformation.workflow;
    if (!workflow) {
        return getBusinessId(generalInformation);
    }
    return workflow;
}

/**
 * Determines if auto name generation should be enabled for a given workflow.
 * @param workflow - The workflow to check.
 * @returns True if auto name generation should be enabled for the workflow, false otherwise.
 */
export const shouldEnableAutoNameGenerationForWorkflow = (workflow: NullableString | undefined): boolean => {
    switch (workflow) {
      case PAC: return true;
      case DCSP_LLE: return true;
      default: break;
    }
    return false;
}

/**
 * Extracts the workflow value from an RFX request based on its properties and business ID.
 *
 * @param rfxRequest - The RFX request object containing properties and business information
 * @returns The workflow value if found in properties, otherwise returns the business ID as default
 */
export const getWorkflowFromBackendModel = (rfxRequest: RFXRequest) => {
    const businessId = getBusinessIdFromBackendModel(rfxRequest);
    const workflow = extractPropertyOrElseDefault(rfxRequest.properties, RequestPropertyIds.Workflow, businessId);
    return workflow;
}

/**
 * Determines if auto BOM import should be executed for a given workflow.
 *
 * @param workflow - The workflow to check.
 * @returns True if auto BOM import should be executed for the given workflow, false otherwise.
 */
export const shouldExecuteAutoBOMImportForWorkflow = (workflow: string): boolean => {
    switch (workflow) {
      case PAC: return true;
      case DCSP_LLE: return true;
      default: break;
    }
    return false;
}
